import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Floating-label text field. Label sits over the input by default and floats
 * up + shrinks when the input is focused or has a value. The visible
 * "placeholder" is the label — the actual `placeholder` attribute is a single
 * space so the CSS `:placeholder-shown` selector behaves predictably.
 *
 * Three validation tones drive border + label + helper color:
 *   - error: red. Pass `error="…"` to flip the field red.
 *   - valid: green. Pass `valid` (boolean) once the input meets requirements.
 *   - neutral: default. Border is `border-input`, label is muted, focus tints
 *     to brand cyan.
 *
 * Helper text and error share the slot below the input (error wins).
 * Radius matches the existing <Input> (`rounded-xl`).
 */

export interface FieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "placeholder"> {
  label: string;
  id: string;
  error?: string;
  helper?: string;
  /** When true, paints the border and label green to indicate the input meets validation. */
  valid?: boolean;
  containerClassName?: string;
}

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      label,
      id,
      error,
      helper,
      valid,
      className,
      containerClassName,
      required,
      type = "text",
      ...props
    },
    ref,
  ) => {
    const helperId = `${id}-helper`;
    const errorId = `${id}-error`;
    const hasError = Boolean(error);
    // Tone of voice: error > valid > neutral. A field can't be both invalid AND valid.
    const tone: "error" | "valid" | "neutral" = hasError ? "error" : valid ? "valid" : "neutral";
    const message = hasError ? error : helper;
    const messageId = hasError ? errorId : helper ? helperId : undefined;

    return (
      <div className={cn("w-full", containerClassName)}>
        {/* Inner wrapper sized to just the input — the absolute label is
            positioned relative to THIS, not the outer one. Without this layer,
            helper/error text below would inflate the box and the resting
            label's top-1/2 would visually slip below the input's midline. */}
        <div className="relative">
          <input
            id={id}
            ref={ref}
            type={type}
            placeholder=" "
            required={required}
            aria-invalid={hasError || undefined}
            aria-describedby={messageId}
            className={cn(
              // Base — h-10 to match the rest of the UI scale, with the
              // top padding reserved for the floated label.
              "peer flex h-10 w-full rounded-xl border bg-background px-3 pt-3 pb-0.5 text-sm leading-tight shadow-sm transition-colors",
              "placeholder:text-transparent",
              "focus-visible:outline-none focus-visible:ring-1",
              "disabled:cursor-not-allowed disabled:opacity-50",
              // Border / ring states by tone
              tone === "error" && "border-danger focus-visible:ring-danger",
              tone === "valid" &&
                "border-success focus-visible:border-success focus-visible:ring-success",
              tone === "neutral" &&
                "border-input focus-visible:border-brand focus-visible:ring-brand",
              className,
            )}
            {...props}
          />
          <label
            htmlFor={id}
            className={cn(
              "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 origin-[0] bg-background px-1 text-[13px] leading-none transition-all",
              // Floats up when focused OR has content.
              "peer-focus:top-0 peer-focus:text-[11px] peer-focus:-translate-y-1/2",
              "peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:-translate-y-1/2",
              tone === "error" && "text-danger peer-focus:text-danger",
              tone === "valid" && "text-success peer-focus:text-success",
              tone === "neutral" && "text-muted-foreground peer-focus:text-brand",
            )}
          >
            {label}
            {required ? <span className="ml-0.5 text-danger">*</span> : null}
          </label>
        </div>
        {message ? (
          <p
            id={messageId}
            className={cn(
              "mt-1 px-1 text-[11px] leading-tight",
              hasError ? "text-danger" : "text-muted-foreground",
            )}
          >
            {message}
          </p>
        ) : null}
      </div>
    );
  },
);
Field.displayName = "Field";
